import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { SafeHtml } from "@/components/SafeHtml";
import {
  Bold, Italic, Underline as UnderlineIcon, List, ListOrdered,
  Heading2, Heading3, Link2, Unlink, Undo, Redo, Eye, FileCode,
  Type, Sparkles, Save, RefreshCw, Plus, X, Search, Image, Tag
} from "lucide-react";
import {
  Popover, PopoverContent, PopoverTrigger
} from "@/components/ui/popover";

const MenuBar = ({ editor }) => {
  const [linkUrl, setLinkUrl] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  if (!editor) return null;

  const setLink = () => {
    if (linkUrl) {
      editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
    }
    setLinkUrl("");
    setLinkOpen(false);
  };

  const buttonClass = (active) =>
    `h-8 w-8 p-0 ${active ? "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300" : "hover:bg-slate-100 dark:hover:bg-slate-800"}`;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-slate-50 dark:bg-slate-900 rounded-t-lg">
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive("bold"))} title="Pogrubienie">
        <Bold className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive("italic"))} title="Kursywa">
        <Italic className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive("underline"))} title="Podkreslenie">
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 2 }))} title="Naglowek H2">
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 3 }))} title="Naglowek H3">
        <Heading3 className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive("bulletList"))} title="Lista punktowana">
        <List className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive("orderedList"))} title="Lista numerowana">
        <ListOrdered className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
      
      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={buttonClass(editor.isActive("link"))} title="Dodaj link">
            <Link2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-72 p-3">
          <div className="space-y-2">
            <p className="text-xs font-medium">Dodaj link</p>
            <Input placeholder="https://example.com" value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)} className="h-8 text-xs"
              onKeyDown={(e) => e.key === "Enter" && setLink()} />
            <Button size="sm" className="w-full h-8 text-xs" onClick={setLink}>Wstaw link</Button>
          </div>
        </PopoverContent>
      </Popover>
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")} className={buttonClass(false)} title="Usun link">
        <Unlink className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 dark:bg-slate-700 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()} className={buttonClass(false)} title="Cofnij">
        <Undo className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()} className={buttonClass(false)} title="Ponow">
        <Redo className="w-4 h-4" />
      </Button>
    </div>
  );
};

export default function ArticleEditor({
  initialTitle = "",
  initialContent = "",
  initialMetaTitle = "",
  initialMetaDesc = "",
  initialTags = [],
  initialImageAlt = "",
  targetPhrase = "",
  targetUrl = "",
  advertiserImage = "",
  onSave,
  onContinue,
  onRegenerate,
  saving = false,
  continuing = false,
  regenerating = false,
}) {
  const [title, setTitle] = useState(initialTitle);
  const [activeTab, setActiveTab] = useState("editor");
  const [htmlSource, setHtmlSource] = useState(initialContent);
  
  // SEO Meta
  const [metaTitle, setMetaTitle] = useState(initialMetaTitle);
  const [metaDesc, setMetaDesc] = useState(initialMetaDesc);
  const [tags, setTags] = useState(initialTags);
  const [newTag, setNewTag] = useState("");
  const [imageAlt, setImageAlt] = useState(initialImageAlt);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { 
          class: "text-blue-600 underline hover:text-blue-800",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Underline,
      Placeholder.configure({ placeholder: "Zacznij pisac artykul..." }),
    ],
    content: initialContent,
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose max-w-none focus:outline-none min-h-[300px] p-4 dark:prose-invert",
      },
    },
    onUpdate: ({ editor }) => setHtmlSource(editor.getHTML()),
  });

  useEffect(() => {
    if (editor && initialContent !== editor.getHTML()) {
      editor.commands.setContent(initialContent);
      setHtmlSource(initialContent);
    }
  }, [initialContent, editor]);

  useEffect(() => { setTitle(initialTitle); }, [initialTitle]);
  useEffect(() => { setMetaTitle(initialMetaTitle); }, [initialMetaTitle]);
  useEffect(() => { setMetaDesc(initialMetaDesc); }, [initialMetaDesc]);
  useEffect(() => { setTags(initialTags || []); }, [initialTags]);
  useEffect(() => { setImageAlt(initialImageAlt); }, [initialImageAlt]);

  const insertPhraseLink = useCallback(() => {
    if (!editor || !targetPhrase || !targetUrl) return;
    editor.chain().focus().insertContent(`<a href="${targetUrl}">${targetPhrase}</a>`).run();
  }, [editor, targetPhrase, targetUrl]);

  const applyHtmlChanges = () => {
    if (editor) editor.commands.setContent(htmlSource);
  };

  const handleSave = () => {
    const content = editor ? editor.getHTML() : htmlSource;
    onSave?.(title, content, metaTitle, metaDesc, tags, imageAlt);
  };

  const addTag = () => {
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag("");
    }
  };

  const removeTag = (tagToRemove) => {
    setTags(tags.filter(t => t !== tagToRemove));
  };

  const charCount = htmlSource.replace(/<[^>]+>/g, "").length;
  const wordCount = htmlSource.replace(/<[^>]+>/g, "").split(/\s+/).filter(w => w).length;

  return (
    <div data-testid="article-editor" className="space-y-4">
      {/* Tytul */}
      <div className="space-y-1">
        <label className="text-xs font-semibold text-slate-600 dark:text-slate-400">Tytul artykulu</label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)}
          className="h-11 text-base font-semibold" placeholder="Wpisz tytul artykulu..."
          data-testid="article-title-input" />
      </div>

      {/* Tabs: WYSIWYG / HTML / Preview / SEO */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full grid grid-cols-4 h-9">
          <TabsTrigger value="editor" className="text-xs gap-1.5">
            <Type className="w-3.5 h-3.5" /> Edytor
          </TabsTrigger>
          <TabsTrigger value="html" className="text-xs gap-1.5">
            <FileCode className="w-3.5 h-3.5" /> HTML
          </TabsTrigger>
          <TabsTrigger value="preview" className="text-xs gap-1.5">
            <Eye className="w-3.5 h-3.5" /> Podglad
          </TabsTrigger>
          <TabsTrigger value="seo" className="text-xs gap-1.5">
            <Search className="w-3.5 h-3.5" /> SEO
          </TabsTrigger>
        </TabsList>

        {/* WYSIWYG Editor */}
        <TabsContent value="editor" className="mt-2">
          <div className="border rounded-lg overflow-hidden bg-white dark:bg-slate-950">
            <MenuBar editor={editor} />
            <EditorContent editor={editor} />
          </div>
        </TabsContent>

        {/* HTML Source */}
        <TabsContent value="html" className="mt-2">
          <textarea value={htmlSource} onChange={(e) => setHtmlSource(e.target.value)}
            className="w-full h-[350px] p-3 text-xs font-mono border rounded-lg bg-slate-50 dark:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500"
            placeholder="Kod HTML artykulu..." data-testid="article-html-textarea" />
          <Button size="sm" variant="outline" className="mt-2 h-8 text-xs" onClick={applyHtmlChanges}>
            Zastosuj zmiany HTML
          </Button>
        </TabsContent>

        {/* Preview - Improved Layout */}
        <TabsContent value="preview" className="mt-2">
          <div className="border rounded-lg bg-white dark:bg-slate-950 overflow-hidden shadow-sm">
            {/* SEO Preview Bar */}
            <div className="bg-slate-100 dark:bg-slate-800 p-3 border-b space-y-2">
              <div className="flex items-center gap-2">
                <Search className="w-4 h-4 text-slate-400" />
                <span className="text-xs text-slate-500">Podglad w wyszukiwarce Google:</span>
              </div>
              <div className="bg-white dark:bg-slate-900 rounded-lg p-3 border">
                <p className="text-blue-600 text-sm font-medium hover:underline cursor-pointer truncate">
                  {metaTitle || title || "Tytul strony"}
                </p>
                <p className="text-green-700 text-xs truncate">epidemos.pl › artykul</p>
                <p className="text-slate-600 dark:text-slate-400 text-xs mt-1 line-clamp-2">
                  {metaDesc || "Brak opisu meta. Dodaj opis w zakladce SEO."}
                </p>
              </div>
            </div>
            
            {/* Article Header */}
            <div className="bg-gradient-to-b from-slate-50 to-white dark:from-slate-900 dark:to-slate-950 p-6 border-b">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white leading-tight mb-3">
                {title || "Tytul artykulu"}
              </h1>
              <div className="flex items-center gap-4 text-xs text-slate-500">
                <span>Autor: Admin</span>
                <span>|</span>
                <span>{new Date().toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" })}</span>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {tags.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px] bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            
            {/* Article Content */}
            <div className="p-6 max-h-[500px] overflow-y-auto">
              <SafeHtml as="article" className="prose prose-slate prose-base max-w-none dark:prose-invert
                prose-headings:font-bold prose-headings:text-slate-800 dark:prose-headings:text-slate-100
                prose-h2:text-xl prose-h2:mt-8 prose-h2:mb-4 prose-h2:pb-2 prose-h2:border-b prose-h2:border-slate-200 dark:prose-h2:border-slate-700
                prose-h3:text-lg prose-h3:mt-6 prose-h3:mb-3
                prose-p:text-slate-600 dark:prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4 prose-p:text-base
                prose-a:text-blue-600 prose-a:no-underline hover:prose-a:underline
                prose-ul:my-4 prose-ul:pl-6 prose-li:my-2
                prose-strong:text-slate-800 dark:prose-strong:text-slate-200
                prose-img:rounded-lg prose-img:shadow-md"
                html={htmlSource || "<p class='text-slate-400 italic'>Brak tresci do podgladu</p>"}
              />
            </div>
            
            {/* Image Alt Preview */}
            {imageAlt && (
              <div className="bg-slate-50 dark:bg-slate-800 p-3 border-t">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Image className="w-4 h-4" />
                  <span>Alt zdjecia:</span>
                  <span className="text-slate-700 dark:text-slate-300">{imageAlt}</span>
                </div>
              </div>
            )}
          </div>
        </TabsContent>

        {/* SEO Tab */}
        <TabsContent value="seo" className="mt-2 space-y-4">
          <div className="grid grid-cols-1 gap-4 p-4 border rounded-lg bg-slate-50 dark:bg-slate-900">
            {/* Meta Title */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                <Search className="w-3.5 h-3.5" /> Meta Tytul (max 60 znakow)
              </label>
              <Input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value.slice(0, 60))}
                className="h-10" placeholder="Tytul SEO widoczny w Google..." />
              <p className={`text-[10px] ${metaTitle.length > 55 ? 'text-amber-600' : 'text-slate-400'}`}>
                {metaTitle.length}/60 znakow
              </p>
            </div>

            {/* Meta Description */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                <FileCode className="w-3.5 h-3.5" /> Meta Opis (max 155 znakow)
              </label>
              <Textarea value={metaDesc} onChange={(e) => setMetaDesc(e.target.value.slice(0, 155))}
                className="h-20 resize-none" placeholder="Opis zachecajacy do klikniecia..." />
              <p className={`text-[10px] ${metaDesc.length > 150 ? 'text-amber-600' : 'text-slate-400'}`}>
                {metaDesc.length}/155 znakow
              </p>
            </div>

            {/* Tags */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                <Tag className="w-3.5 h-3.5" /> Tagi artykulu
              </label>
              <div className="flex gap-2">
                <Input value={newTag} onChange={(e) => setNewTag(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())}
                  className="h-9 flex-1" placeholder="Dodaj tag..." />
                <Button size="sm" variant="outline" className="h-9" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {tags.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-xs gap-1 pr-1">
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-red-500 ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* Image Alt */}
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
                <Image className="w-3.5 h-3.5" /> Alt zdjecia (dla SEO)
              </label>
              <Input value={imageAlt} onChange={(e) => setImageAlt(e.target.value.slice(0, 100))}
                className="h-10" placeholder="Opis obrazka dla wyszukiwarek..." />
              <p className="text-[10px] text-slate-400">{imageAlt.length}/100 znakow</p>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Statystyki */}
      <div className="flex items-center gap-4 text-xs text-slate-500">
        <span>{charCount} znakow</span>
        <span>{wordCount} slow</span>
        {charCount < 3500 && (
          <span className="text-amber-600">(minimum 3500 - brakuje {3500 - charCount})</span>
        )}
        {charCount >= 3500 && <span className="text-emerald-600">OK</span>}
      </div>

      {/* Link reklamodawcy */}
      {targetPhrase && targetUrl && (
        <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-400">Link reklamodawcy</p>
              <p className="text-xs text-emerald-600 dark:text-emerald-500 mt-0.5">
                Fraza: <strong>{targetPhrase}</strong> → {targetUrl}
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={insertPhraseLink}
              className="h-8 text-xs gap-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100">
              <Plus className="w-3 h-3" /> Wstaw link
            </Button>
          </div>
        </div>
      )}

      {/* Podglad obrazka reklamodawcy */}
      {advertiserImage && (
        <div className="p-4 rounded-lg bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-2 mb-3">
            <Image className="w-4 h-4 text-blue-600" />
            <p className="text-xs font-semibold text-slate-700 dark:text-slate-300">
              Zdjecie glowne do artykulu (wybrane przez reklamodawce)
            </p>
          </div>
          <div className="relative bg-white dark:bg-slate-800 rounded-lg border overflow-hidden">
            <img 
              src={advertiserImage} 
              alt={imageAlt || "Zdjecie reklamodawcy"}
              className="w-full max-h-64 object-contain"
              onError={(e) => { 
                e.target.style.display = 'none'; 
                e.target.parentElement.innerHTML = '<p class="text-xs text-slate-500 p-4 text-center">Nie udalo sie wczytac obrazka</p>';
              }}
            />
          </div>
          <p className="text-[10px] text-slate-500 mt-2">
            To zdjecie zostanie uzyte jako glowna grafika artykulu przy publikacji
          </p>
        </div>
      )}

      {/* Przyciski akcji */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button size="sm" className="h-9 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
          onClick={handleSave} disabled={saving} data-testid="save-article-btn">
          <Save className="w-3.5 h-3.5" />
          {saving ? "Zapisywanie..." : "Zapisz wszystko"}
        </Button>

        {onContinue && (
          <Button size="sm" variant="outline" onClick={onContinue} disabled={continuing}
            className="h-9 gap-1.5 border-violet-300 text-violet-700 hover:bg-violet-50"
            data-testid="continue-article-btn">
            <Sparkles className="w-3.5 h-3.5" />
            {continuing ? "AI pisze..." : "Dokoncz AI (+1500 zn)"}
          </Button>
        )}

        {onRegenerate && (
          <Button size="sm" variant="outline" onClick={onRegenerate} disabled={regenerating}
            className="h-9 gap-1.5 text-amber-700 border-amber-300 hover:bg-amber-50"
            data-testid="regenerate-article-btn">
            <RefreshCw className="w-3.5 h-3.5" />
            {regenerating ? "Generowanie..." : "Regeneruj"}
          </Button>
        )}
      </div>
    </div>
  );
}
